import { Button, Col, Form, message } from 'antd';
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import { useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useWindowSize } from 'react-use';
import LOGO from '../../assets/svg/vastu-logo.svg';
import { LOCAL_STORAGE, ROUTES, TIMER } from '../../common/constants';
import {
  getItem,
  isMobileBrowser,
  removeItem,
  setItem,
} from '../../common/utils';
import { customizeRequiredMark } from '../../components/CustomRequiredMark';
import useRouter from '../../hooks/useRouter';
import '../auth/auth.less';
import { initiate } from './OtpLess';

const LoginForm = () => {
  const [mobileNumberForm] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const { navigate } = useRouter();
  const { width } = useWindowSize();

  const validatePhoneNumber = (_V: object, value: string) => {
    if (!value) {
      return Promise.reject(new Error('Phone number is required'));
    }

    const phoneNumber = `+${value.replace(/\D/g, '')}`;
    if (isValidPhoneNumber(phoneNumber)) {
      return Promise.resolve();
    } else {
      return Promise.reject(new Error('Invalid phone number'));
    }
  };

  const handleNextStep = async (value: { phone: string }) => {
    setLoading(true);
    try {
      const phoneNumber = `+${value?.phone?.replace(/\D/g, '')}`;
      const isNewNumber = parsePhoneNumber(phoneNumber);

      const storedNumber = localStorage.getItem(
        LOCAL_STORAGE.LAST_PHONE_NUMBER,
      );
      const blockTime = parseInt(
        getItem(LOCAL_STORAGE.OTP_BLOCK_TIME) || '0',
        10,
      );
      const currentTime = Date.now();

      const otpStartTime = parseInt(
        getItem(LOCAL_STORAGE.OTP_START_TIME) || '0',
        10,
      );
      const otpDuration =
        parseInt(getItem(LOCAL_STORAGE.OTP_TIMER) || '0', 10) * 1000;
      const timeRemaining = otpStartTime + otpDuration - currentTime;

      if (
        blockTime &&
        currentTime < blockTime &&
        storedNumber === value.phone
      ) {
        const remainingMinutes = Math.ceil((blockTime - currentTime) / 60000);
        message.error(
          `Too many attempts! Try again in ${remainingMinutes} minutes`,
        );
        setLoading(false);
        return;
      }

      if (storedNumber === value.phone && timeRemaining > 0) {
        navigate(`${ROUTES.VERIFY_OTP}?number=${value.phone}`);
        setLoading(false);
        return;
      }

      if (storedNumber !== value.phone) {
        removeItem(LOCAL_STORAGE.OTP_ATTEMPTS);
        removeItem(LOCAL_STORAGE.OTP_BLOCK_TIME);
      }

      await initiate(
        isNewNumber?.nationalNumber,
        isNewNumber?.countryCallingCode,
        TIMER.INITIAL_TIME_SECONDS,
      );

      const currentTimeNew = Date.now();
      setItem(LOCAL_STORAGE.OTP_START_TIME, currentTimeNew.toString());
      setItem(LOCAL_STORAGE.OTP_TIMER, TIMER.INITIAL_TIME_SECONDS.toString());
      localStorage.setItem(LOCAL_STORAGE.LAST_PHONE_NUMBER, value.phone);

      navigate(`${ROUTES.VERIFY_OTP}?number=${value.phone}`);
    } catch (_E) {
      message.error('Error initiating OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Col
      md={12}
      lg={12}
      xl={12}
      xxl={12}
      sm={24}
      xs={24}
      className="login-form-container"
    >
      {isMobileBrowser(width) && (
        <div className="logo-wrapper p-0">
          <img src={LOGO} alt="logo" className="main-logo"></img>
        </div>
      )}
      <Form
        className="login-form-div"
        layout="vertical"
        form={mobileNumberForm}
        onFinish={handleNextStep}
        requiredMark={customizeRequiredMark}
      >
        <div className="login-form">
          <h1 className="phone-input-heading title-lg semibold ">
            What is your Phone Number?
          </h1>
          <Form.Item
            name="phone"
            label={<span className=" medium">Phone Number</span>}
            rules={[{ validator: validatePhoneNumber }]}
            required
          >
            <PhoneInput
              inputProps={{
                autoFocus: true,
                onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    mobileNumberForm.submit();
                  }
                },
              }}
              enableSearch={true}
              country={'in'}
              onChange={(value) =>
                mobileNumberForm.setFieldsValue({ phone: value })
              }
              countryCodeEditable={false}
            />
          </Form.Item>
        </div>
        <div className="login-button-div">
          <Form.Item>
            <Button
              className="login-button medium"
              htmlType="submit"
              loading={loading}
            >
              Get OTP
            </Button>
          </Form.Item>
        </div>
      </Form>
    </Col>
  );
};

export default LoginForm;
